/**
 * SimpleRichTextEditor — WYSIWYG minimalista para o admin editar emails
 * sem tocar em HTML. Botões: negrito/itálico/sublinhado, títulos, listas,
 * alinhamento, link, cor. Dropdown "Inserir placeholder" injeta chaves
 * como {{stk_id}}, {{total}}, etc.
 *
 * Notas:
 *   • Interno: contentEditable div. Emite `onChange(html)` sempre que o
 *     utilizador escreve. Aceita valor externo controlado (reset via key).
 *   • `document.execCommand` está deprecated mas continua a ser suportado
 *     em TODOS os browsers relevantes (>99% cobertura) — a alternativa
 *     seria adicionar dep externa (Quill/TipTap) e complexidade a mais.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered,
  Link as LinkIcon, AlignLeft, AlignCenter, Palette, Code2, MoveVertical,
  Table as TableIcon, Type as TypeIcon,
} from "lucide-react";

const TOOLBAR_BTN =
  "w-8 h-8 grid place-items-center rounded hover:bg-slate-200 active:bg-slate-300 text-slate-700 disabled:opacity-40";

function exec(cmd, value = null) {
  try { document.execCommand(cmd, false, value); } catch { /* ignore */ }
}

const PLACEHOLDERS = [
  { key: "stk_id", label: "ID inscrição (STK-42)" },
  { key: "school", label: "Nome da escola" },
  { key: "count", label: "Nº de credenciais" },
  { key: "price", label: "Preço por credencial" },
  { key: "total", label: "Total a pagar" },
  { key: "credentials_table", label: "Tabela de credenciais" },
  { key: "payment_methods", label: "Métodos de pagamento" },
  { key: "paid_at", label: "Data do pagamento" },
];

const COLORS = ["#0f172a", "#1e40af", "#059669", "#d97706", "#dc2626", "#7c3aed", "#64748b"];

// Common line-height presets + manual adjustment. Applied as inline
// `line-height` on the block elements so it persists in the saved HTML and
// renders identically on the public page.
const LINE_HEIGHTS = [
  { label: "Compacto", value: "1.3" },
  { label: "Normal", value: "1.6" },
  { label: "Largo", value: "1.9" },
  { label: "Muito largo", value: "2.4" },
];
const BLOCK_SELECTOR = "p,h1,h2,h3,h4,h5,h6,div,li,ul,ol,blockquote";

// Common font sizes (applied as inline font-size on the selected text).
const FONT_SIZES = [
  { label: "Pequeno", value: "13px" },
  { label: "Normal", value: "16px" },
  { label: "Médio", value: "18px" },
  { label: "Grande", value: "22px" },
  { label: "Muito grande", value: "28px" },
  { label: "Enorme", value: "36px" },
];

export default function SimpleRichTextEditor({
  value = "",
  onChange,
  placeholder = "Escreve o teu texto…",
  minHeight = 220,
  maxHeight = "60vh",
  testId,
  placeholders = PLACEHOLDERS,
}) {
  const ref = useRef(null);
  const [showPlaceholderMenu, setShowPlaceholderMenu] = useState(false);
  const [showColorMenu, setShowColorMenu] = useState(false);
  const [showFontSizeMenu, setShowFontSizeMenu] = useState(false);
  const [manualFontSize, setManualFontSize] = useState(16);
  const [showLineHeightMenu, setShowLineHeightMenu] = useState(false);
  const [manualLh, setManualLh] = useState("1.6");
  const [showTableMenu, setShowTableMenu] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [tableHeader, setTableHeader] = useState(true);
  const [tableScale, setTableScale] = useState(100);
  const savedRangeRef = useRef(null);

  // Only sync from `value` prop when the underlying HTML differs — otherwise
  // typing would reset the caret to position 0 on every keystroke.
  useEffect(() => {
    if (!ref.current) return;
    if (ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const emit = () => {
    if (!ref.current) return;
    onChange?.(ref.current.innerHTML);
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      // Only remember selections that live inside THIS editor. Mousedowns on
      // toolbar inputs (table size, line-height) would otherwise overwrite the
      // saved range with a selection in those inputs, breaking insertHTML.
      if (ref.current && ref.current.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range.cloneRange();
      }
    }
  };

  // Put the caret at the very end of the editor content. Used as a fallback
  // when there is no valid saved selection inside the editor.
  const caretToEnd = () => {
    const root = ref.current;
    if (!root) return;
    const range = document.createRange();
    range.selectNodeContents(root);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  };

  const selectionInEditor = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return false;
    return !!ref.current && ref.current.contains(sel.getRangeAt(0).commonAncestorContainer);
  };

  const restoreSelection = () => {
    if (!savedRangeRef.current) return;
    const sel = window.getSelection();
    if (!sel) return;
    sel.removeAllRanges();
    sel.addRange(savedRangeRef.current);
  };

  const applyCmd = (cmd, val = null) => {
    if (ref.current) ref.current.focus();
    restoreSelection();
    exec(cmd, val);
    emit();
  };

  const insertPlaceholder = (key) => {
    if (ref.current) ref.current.focus();
    restoreSelection();
    exec("insertText", `{{${key}}}`);
    setShowPlaceholderMenu(false);
    emit();
  };

  const insertLink = () => {
    const url = window.prompt("URL (https://…)");
    if (url) applyCmd("createLink", url);
  };

  const applyColor = (color) => {
    applyCmd("foreColor", color);
    setShowColorMenu(false);
  };

  // Apply a font-size to the selected text. Wraps each selected text node in a
  // styled <span> (never a block element) so multi-line selections stay valid.
  const wrapTextRange = (node, start, end, value) => {
    if (start >= end) return;
    const r = document.createRange();
    r.setStart(node, start);
    r.setEnd(node, end);
    const span = document.createElement("span");
    span.style.fontSize = value;
    try { r.surroundContents(span); } catch { /* skip un-wrappable fragment */ }
  };

  const applyFontSize = (size) => {
    const root = ref.current;
    if (!root) return;
    const value = typeof size === "number" ? `${size}px` : String(size);
    root.focus();
    restoreSelection();
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || sel.isCollapsed || !selectionInEditor()) {
      setShowFontSizeMenu(false);
      return;
    }
    const range = sel.getRangeAt(0);
    if (range.startContainer === range.endContainer && range.startContainer.nodeType === 3) {
      wrapTextRange(range.startContainer, range.startOffset, range.endOffset, value);
    } else {
      const base = range.commonAncestorContainer;
      const walkRoot = base.nodeType === 3 ? base.parentNode : base;
      const walker = document.createTreeWalker(walkRoot, NodeFilter.SHOW_TEXT, {
        acceptNode: (n) =>
          range.intersectsNode(n) && n.textContent.trim()
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT,
      });
      const nodes = [];
      let tn;
      while ((tn = walker.nextNode())) nodes.push(tn);
      // Wrap last→first so earlier text offsets stay valid.
      nodes.reverse().forEach((node) => {
        const start = node === range.startContainer ? range.startOffset : 0;
        const end = node === range.endContainer ? range.endOffset : node.textContent.length;
        wrapTextRange(node, start, end, value);
      });
    }
    setShowFontSizeMenu(false);
    saveSelection();
    emit();
  };

  const applyHeading = (tag) => {
    applyCmd("formatBlock", tag);
  };

  // Wrap any direct child nodes of `root` that are NOT block elements (bare
  // text nodes, inline elements, <br>) into <div> blocks. This guarantees the
  // very first typed line — which contentEditable leaves as a bare text node —
  // becomes a styleable block so line-height applies (and persists in HTML).
  const normalizeBareNodes = (root) => {
    const BLOCK = new Set(["P","H1","H2","H3","H4","H5","H6","DIV","UL","OL","LI","BLOCKQUOTE","TABLE"]);
    let run = [];
    const flush = () => {
      if (!run.length) return;
      const hasContent = run.some((n) =>
        (n.nodeType === 3 && n.textContent.trim()) ||
        (n.nodeType === 1));
      if (hasContent) {
        const div = document.createElement("div");
        run[0].parentNode.insertBefore(div, run[0]);
        run.forEach((n) => div.appendChild(n));
      }
      run = [];
    };
    Array.from(root.childNodes).forEach((node) => {
      const isBlock = node.nodeType === 1 && BLOCK.has(node.tagName);
      if (isBlock) flush();
      else run.push(node);
    });
    flush();
  };

  // Apply line-height to the block(s) touched by the selection. When the
  // selection is collapsed (just a caret) it applies to EVERY block in the
  // editor — handy to set a document-wide standard in one click.
  const applyLineHeight = (rawLh) => {
    const root = ref.current;
    if (!root) return;
    // Clamp to a sensible range (1–3) so manual typing can't produce 0/50/-1.
    let n = parseFloat(rawLh);
    if (!isFinite(n)) n = 1.6;
    n = Math.min(3, Math.max(1, n));
    const lh = String(n);
    root.focus();
    restoreSelection();
    const sel = window.getSelection();
    const hadSelection = sel && sel.rangeCount > 0 && !sel.isCollapsed;
    // Only normalize (wrap bare nodes) when applying document-wide — a live
    // text selection must not have its ranges invalidated by DOM surgery.
    if (!hadSelection) normalizeBareNodes(root);
    const all = Array.from(root.querySelectorAll(BLOCK_SELECTOR));
    let targets = [];
    if (hadSelection) {
      const range = sel.getRangeAt(0);
      targets = all.filter((el) => range.intersectsNode(el));
    }
    if (targets.length === 0) targets = all;
    if (targets.length === 0) {
      root.style.lineHeight = lh;
    } else {
      targets.forEach((el) => { el.style.lineHeight = lh; });
    }
    emit();
  };

  // Insert a table at the caret. Inline styles give visible gridlines in the
  // editor, the public page and print, regardless of external CSS.
  const insertTable = () => {
    const root = ref.current;
    if (!root) return;
    const rows = Math.max(1, Math.min(30, parseInt(tableRows, 10) || 1));
    const cols = Math.max(1, Math.min(12, parseInt(tableCols, 10) || 1));
    const cellStyle = "border:1px solid #cbd5e1;padding:6px 8px;min-width:48px;vertical-align:top;";
    const thStyle = "border:1px solid #cbd5e1;padding:6px 8px;min-width:48px;background:#f1f5f9;font-weight:600;text-align:left;";
    let html = '<table style="border-collapse:collapse;width:100%;margin:10px 0;">';
    for (let r = 0; r < rows; r++) {
      html += "<tr>";
      for (let col = 0; col < cols; col++) {
        const isHeader = tableHeader && r === 0;
        html += isHeader ? `<th style="${thStyle}"><br></th>` : `<td style="${cellStyle}"><br></td>`;
      }
      html += "</tr>";
    }
    html += "</table><p><br></p>";
    root.focus();
    // Prefer the caret saved from inside the editor; otherwise drop it at the
    // end so the table always lands in the editable area (not in a toolbar
    // input that had focus when the menu was used).
    restoreSelection();
    if (!selectionInEditor()) caretToEnd();
    document.execCommand("insertHTML", false, html);
    // Chrome's insertHTML strips some inline styles — re-apply them on the DOM
    // so the saved HTML keeps its own gridlines (print/email contexts too).
    root.querySelectorAll("table").forEach((tbl) => {
      if (!tbl.style.borderCollapse) {
        tbl.style.borderCollapse = "collapse";
        tbl.style.width = "100%";
        tbl.style.margin = "10px 0";
      }
      tbl.querySelectorAll("th,td").forEach((cell) => {
        cell.style.border = "1px solid #cbd5e1";
        cell.style.padding = "6px 8px";
        if (cell.tagName === "TH") {
          cell.style.background = "#f1f5f9";
          cell.style.fontWeight = "600";
          cell.style.textAlign = "left";
        }
      });
    });
    emit();
  };

  // Find the <table> that contains the current selection (caret), falling
  // back to the last table in the editor.
  const tableAtCaret = () => {
    const root = ref.current;
    if (!root) return null;
    const sel = window.getSelection();
    if (sel && sel.rangeCount && selectionInEditor()) {
      let node = sel.getRangeAt(0).commonAncestorContainer;
      let el = node.nodeType === 1 ? node : node.parentElement;
      const t = el ? el.closest("table") : null;
      if (t && root.contains(t)) return t;
    }
    const tables = root.querySelectorAll("table");
    return tables[tables.length - 1] || null;
  };

  // Scale a table (and everything inside it — text + cell width/height) to a
  // percentage of its original size using CSS `zoom`, which reflows cleanly.
  // Because the table is width:100% (responsive), zoom alone won't shrink its
  // width; so we also pin an explicit px width (= full width at 100%) which
  // zoom then scales, making the WHOLE table scale proportionally.
  const scaleTable = (pct) => {
    const root = ref.current;
    if (!root) return;
    root.focus();
    restoreSelection();
    const table = tableAtCaret();
    if (!table) return;
    let n = parseFloat(pct);
    if (!isFinite(n)) n = 100;
    n = Math.min(300, Math.max(10, n));
    if (n === 100) {
      table.style.removeProperty("zoom");
      table.style.removeProperty("width");
    } else {
      // Measure the full (unscaled) width first, then pin it so zoom scales it.
      table.style.removeProperty("zoom");
      table.style.removeProperty("width");
      const baseW = table.offsetWidth;
      if (baseW > 0) table.style.width = `${baseW}px`;
      table.style.zoom = String(n / 100);
    }
    emit();
  };

  return (
    <div className="border border-slate-200 rounded-md bg-white" data-testid={testId}>
      <div className="flex items-center gap-0.5 border-b border-slate-200 bg-slate-50 p-1 flex-wrap sticky top-0 z-20 rounded-t-md"
        onMouseDown={(e) => { saveSelection(); if (e.target === e.currentTarget) e.preventDefault(); }}>
        <button type="button" className={TOOLBAR_BTN} onClick={() => applyCmd("bold")} title="Negrito"><Bold className="w-4 h-4" /></button>
        <button type="button" className={TOOLBAR_BTN} onClick={() => applyCmd("italic")} title="Itálico"><Italic className="w-4 h-4" /></button>
        <button type="button" className={TOOLBAR_BTN} onClick={() => applyCmd("underline")} title="Sublinhado"><Underline className="w-4 h-4" /></button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" className={TOOLBAR_BTN} onClick={() => applyHeading("h2")} title="Título grande"><Heading2 className="w-4 h-4" /></button>
        <button type="button" className={TOOLBAR_BTN} onClick={() => applyHeading("h3")} title="Subtítulo"><Heading3 className="w-4 h-4" /></button>
        <button type="button" className={TOOLBAR_BTN} onClick={() => applyHeading("p")} title="Parágrafo"><span className="text-xs font-bold">P</span></button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" className={TOOLBAR_BTN} onClick={() => applyCmd("insertUnorderedList")} title="Lista"><List className="w-4 h-4" /></button>
        <button type="button" className={TOOLBAR_BTN} onClick={() => applyCmd("insertOrderedList")} title="Lista numerada"><ListOrdered className="w-4 h-4" /></button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" className={TOOLBAR_BTN} onClick={() => applyCmd("justifyLeft")} title="Esquerda"><AlignLeft className="w-4 h-4" /></button>
        <button type="button" className={TOOLBAR_BTN} onClick={() => applyCmd("justifyCenter")} title="Centrado"><AlignCenter className="w-4 h-4" /></button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" className={TOOLBAR_BTN} onClick={insertLink} title="Link"><LinkIcon className="w-4 h-4" /></button>
        <div className="relative">
          <button type="button" className={TOOLBAR_BTN}
            onClick={() => { setShowTableMenu((v) => !v); setShowColorMenu(false); setShowLineHeightMenu(false); setShowPlaceholderMenu(false); }}
            title="Inserir tabela" data-testid="table-btn">
            <TableIcon className="w-4 h-4" />
          </button>
          {showTableMenu && (
            <div className="absolute z-30 top-9 left-0 bg-white border border-slate-200 rounded-md shadow-lg p-3 w-56" data-testid="table-menu">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-2">Inserir tabela</div>
              <div className="flex items-center gap-2 mb-2">
                <label className="text-xs text-slate-600 w-16">Linhas</label>
                <input type="number" min="1" max="30" value={tableRows}
                  onMouseDown={(e) => e.stopPropagation()}
                  onChange={(e) => setTableRows(e.target.value)}
                  className="w-16 border border-slate-200 rounded px-2 py-1 text-xs" data-testid="table-rows-input" />
              </div>
              <div className="flex items-center gap-2 mb-2">
                <label className="text-xs text-slate-600 w-16">Colunas</label>
                <input type="number" min="1" max="12" value={tableCols}
                  onMouseDown={(e) => e.stopPropagation()}
                  onChange={(e) => setTableCols(e.target.value)}
                  className="w-16 border border-slate-200 rounded px-2 py-1 text-xs" data-testid="table-cols-input" />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-600 mb-3 cursor-pointer">
                <input type="checkbox" checked={tableHeader}
                  onMouseDown={(e) => e.stopPropagation()}
                  onChange={(e) => setTableHeader(e.target.checked)}
                  className="w-3.5 h-3.5 accent-teal-600" data-testid="table-header-check" />
                Primeira linha como cabeçalho
              </label>
              <button type="button"
                className="w-full px-2 py-1.5 text-xs rounded bg-slate-900 text-white hover:bg-slate-800"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { insertTable(); setShowTableMenu(false); }}
                data-testid="table-insert-apply">Inserir</button>
              <div className="border-t border-slate-100 mt-3 pt-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Escala da tabela (% do original)</div>
                <p className="text-[10px] text-slate-400 mb-2">Clica dentro de uma tabela e escolhe a escala. Ajusta texto e células.</p>
                <div className="flex flex-wrap gap-1 mb-2">
                  {[50, 75, 100, 125, 150].map((p) => (
                    <button key={p} type="button"
                      className="px-2 py-1 text-[11px] rounded border border-slate-200 text-slate-700 hover:bg-blue-50"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { scaleTable(p); setTableScale(p); }}
                      data-testid={`table-scale-${p}`}>{p}%</button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input type="number" min="10" max="300" step="5" value={tableScale}
                    onMouseDown={(e) => e.stopPropagation()}
                    onChange={(e) => setTableScale(e.target.value)}
                    className="w-16 border border-slate-200 rounded px-2 py-1 text-xs" data-testid="table-scale-input" />
                  <span className="text-xs text-slate-500">%</span>
                  <button type="button"
                    className="px-2 py-1 text-xs rounded bg-slate-900 text-white hover:bg-slate-800"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => scaleTable(tableScale)}
                    data-testid="table-scale-apply">Aplicar</button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="relative">
          <button type="button" className={TOOLBAR_BTN} onClick={() => { setShowColorMenu((v) => !v); setShowFontSizeMenu(false); setShowPlaceholderMenu(false); }} title="Cor do texto" data-testid="color-btn">
            <Palette className="w-4 h-4" />
          </button>
          {showColorMenu && (
            <div className="absolute z-30 top-9 left-0 bg-white border border-slate-200 rounded-md shadow-lg p-2" data-testid="color-menu">
              <div className="flex gap-1">
                {COLORS.map((c) => (
                  <button key={c} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => applyColor(c)}
                    className="w-6 h-6 rounded-full border border-slate-300 hover:scale-110 transition-transform"
                    style={{ background: c }} title={c} data-testid={`color-swatch-${c}`} />
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
                <span className="text-[11px] text-slate-500">Personalizada</span>
                <input type="color" defaultValue="#0f172a"
                  onMouseDown={(e) => e.stopPropagation()}
                  onChange={(e) => applyCmd("foreColor", e.target.value)}
                  className="w-7 h-7 p-0 border border-slate-200 rounded cursor-pointer" data-testid="color-custom-input" />
              </div>
            </div>
          )}
        </div>
        <div className="relative">
          <button type="button" className={TOOLBAR_BTN} onClick={() => { setShowFontSizeMenu((v) => !v); setShowColorMenu(false); setShowPlaceholderMenu(false); }} title="Tamanho da fonte" data-testid="fontsize-btn">
            <TypeIcon className="w-4 h-4" />
          </button>
          {showFontSizeMenu && (
            <div className="absolute z-30 top-9 left-0 bg-white border border-slate-200 rounded-md shadow-lg py-1 w-44" data-testid="fontsize-menu">
              {FONT_SIZES.map((o) => (
                <button key={o.value} type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applyFontSize(o.value)}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 flex items-center justify-between"
                  data-testid={`fontsize-preset-${o.value}`}>
                  <span className="text-slate-700">{o.label}</span>
                  <code className="text-slate-400 font-mono text-[10px]">{o.value}</code>
                </button>
              ))}
              <div className="border-t border-slate-100 mt-1 pt-2 px-3 pb-1">
                <label className="text-[10px] uppercase tracking-wide text-slate-400">Manual (px)</label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="number" min="8" max="96" step="1" value={manualFontSize}
                    onMouseDown={(e) => e.stopPropagation()}
                    onChange={(e) => setManualFontSize(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyFontSize(`${manualFontSize}px`); } }}
                    className="w-16 border border-slate-200 rounded px-2 py-1 text-xs" data-testid="fontsize-manual-input" />
                  <button type="button"
                    className="px-2 py-1 text-xs rounded bg-slate-900 text-white hover:bg-slate-800"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyFontSize(`${manualFontSize}px`)}
                    data-testid="fontsize-manual-apply">Aplicar</button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <div className="relative">
          <button type="button" className={TOOLBAR_BTN}
            onClick={() => { setShowLineHeightMenu((v) => !v); setShowColorMenu(false); setShowPlaceholderMenu(false); }}
            title="Entrelinha (espaçamento entre linhas)" data-testid="lineheight-btn">
            <MoveVertical className="w-4 h-4" />
          </button>
          {showLineHeightMenu && (
            <div className="absolute z-30 top-9 left-0 bg-white border border-slate-200 rounded-md shadow-lg py-1 w-48" data-testid="lineheight-menu">
              {LINE_HEIGHTS.map((o) => (
                <button key={o.value} type="button"
                  onClick={() => { applyLineHeight(o.value); setManualLh(o.value); setShowLineHeightMenu(false); }}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 flex items-center justify-between"
                  data-testid={`lineheight-preset-${o.value}`}>
                  <span className="text-slate-700">{o.label}</span>
                  <code className="text-slate-400 font-mono text-[10px]">{o.value}</code>
                </button>
              ))}
              <div className="border-t border-slate-100 mt-1 pt-2 px-3 pb-1">
                <label className="text-[10px] uppercase tracking-wide text-slate-400">Manual</label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="number" min="1" max="3" step="0.05" value={manualLh}
                    onMouseDown={(e) => e.stopPropagation()}
                    onChange={(e) => setManualLh(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyLineHeight(String(manualLh || "1.6")); setShowLineHeightMenu(false); } }}
                    className="w-16 border border-slate-200 rounded px-2 py-1 text-xs"
                    data-testid="lineheight-manual-input" />
                  <button type="button"
                    className="px-2 py-1 text-xs rounded bg-slate-900 text-white hover:bg-slate-800"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { applyLineHeight(String(manualLh || "1.6")); setShowLineHeightMenu(false); }}
                    data-testid="lineheight-manual-apply">Aplicar</button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="flex-1" />
        {placeholders && placeholders.length > 0 && (
        <div className="relative">
          <button type="button"
            className="px-2 h-8 rounded text-xs font-semibold text-blue-700 hover:bg-blue-100 flex items-center gap-1"
            onClick={() => { setShowPlaceholderMenu((v) => !v); setShowColorMenu(false); }}>
            <Code2 className="w-3.5 h-3.5" /> Inserir campo
          </button>
          {showPlaceholderMenu && (
            <div className="absolute z-30 right-0 top-9 bg-white border border-slate-200 rounded-md shadow-lg py-1 w-64 max-h-80 overflow-y-auto">
              {placeholders.map((p) => (
                <button key={p.key} type="button" onClick={() => insertPlaceholder(p.key)}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 flex items-center justify-between">
                  <span className="text-slate-700">{p.label}</span>
                  <code className="text-blue-600 font-mono text-[10px]">{`{{${p.key}}}`}</code>
                </button>
              ))}
            </div>
          )}
        </div>
        )}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={saveSelection}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        className="p-3 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset overflow-y-auto [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-2 [&_h2]:mb-1 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-1 [&_a]:text-blue-600 [&_a]:underline [&_table]:border-collapse [&_table]:w-full [&_table]:my-2 [&_th]:border [&_th]:border-slate-300 [&_th]:bg-slate-100 [&_th]:p-1.5 [&_th]:text-left [&_th]:font-semibold [&_td]:border [&_td]:border-slate-300 [&_td]:p-1.5 [&_td]:align-top"
        style={{ minHeight, maxHeight, whiteSpace: "pre-wrap" }}
        data-empty-placeholder={placeholder}
      />
    </div>
  );
}
